import React from 'react'
import { Link, useParams, useLocation } from 'react-router-dom'
import VideoPlayer from 'js/pages/NutritionExplained/components/VideoPlayer'
import VideoThumb from 'js/pages/NutritionExplained/components/VideoThumb'
import Logo from 'js/components/Logo'
import { FormattedDate, FormattedMessage } from 'react-intl'
import convert from 'htmr'
import { ga } from 'js/analytics/adapters'
import { WebinarQuery, WebinarQuery_allWebinars } from './types/WebinarQuery'

let hasTracked = false

type Props = {
  webinars: WebinarQuery['allWebinars']
}

function WebinarVideos({ webinars }: Props) {
  const { video } = useParams()
  const currentWebinar = webinars[
    Number(video) - 1 || 0
  ] as WebinarQuery_allWebinars

  const { pathname } = useLocation()

  // Guard clause work around for the generated datocms types adding null to
  // valid values for all fields (even required) see:
  // https://community.datocms.com/t/make-required-fields-non-nullable-in-graphql-schema/1873
  if (
    !currentWebinar.video ||
    !currentWebinar.publishDateTime ||
    !currentWebinar.description
  ) {
    throw new Error(
      'WebinarVideos currentWebinar missing required data: ${JSON.stringify(currentWebinar)}',
    )
  }

  const isCurrentActive =
    currentWebinar.video &&
    currentWebinar.publishDateTime &&
    Date.now() - new Date(currentWebinar.publishDateTime).getTime() >= 0

  return (
    <div className="h-full">
      <div className="bg-BgSub pt-16 text-center">
        <Logo />
      </div>
      <div className="bg-BgSub px-24 py-48 lg:px-96">
        <div className="mx-auto max-w-968">
          {currentWebinar.video && isCurrentActive ? (
            <div>
              <VideoPlayer
                {...currentWebinar.video}
                id={currentWebinar.video.id as string}
                subheading={
                  <FormattedMessage
                    id="webinar_episode"
                    defaultMessage="Episode {number}"
                    values={{ number: video || 1 }}
                  />
                }
                name={currentWebinar.title}
                description={
                  <p className="s-text pb-16 pt-16">
                    {convert(currentWebinar.description, {
                      transform: {
                        a: function Anchor(props) {
                          return (
                            <a
                              {...props}
                              onClick={() => {
                                ga.track('clicked_webinar_discount')
                              }}
                            />
                          )
                        },
                      },
                    })}
                  </p>
                }
                onPlay={() => {
                  if (hasTracked) return
                  hasTracked = true
                  ga.track('clicked_webinar_video', { Video: pathname })
                }}
              />
            </div>
          ) : (
            <div className="m-64 flex flex-col items-center justify-center py-72 text-24">
              <div className="text mb-8 font-demibold ">
                <FormattedMessage id="webinar_released" />
              </div>
              <FormattedDate
                value={currentWebinar.publishDateTime}
                month="long"
                day="numeric"
              />
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 px-20 py-36 md:grid-cols-2 lg:grid-cols-4">
        {webinars.map(
          ({ video: linkedVideo, publishDateTime, title }, index) => {
            if (!linkedVideo) {
              throw new Error(
                `WebinarVideos missing required prop video, title: ${title}`,
              )
            }
            return (
              <div className="relative mb-12 w-full" key={index}>
                <Link to={`../videos/${index + 1}`}>
                  <VideoThumb
                    video={linkedVideo}
                    name={title}
                    subheading={
                      <FormattedMessage
                        id="webinar_episode"
                        defaultMessage="Episode {number}"
                        values={{ number: index + 1 }}
                      />
                    }
                    placeholder={
                      !linkedVideo && (
                        <FormattedDate
                          value={publishDateTime}
                          month="long"
                          day="numeric"
                        />
                      )
                    }
                  />
                </Link>
              </div>
            )
          },
        )}
      </div>
    </div>
  )
}

export default WebinarVideos
